import { useState, useEffect, useRef } from "react"

interface UseCountUpProps {
  end: number
  duration?: number
  decimals?: number
  startOnView?: boolean
}

export function useCountUp({ end, duration = 2000, decimals = 0, startOnView = true }: UseCountUpProps) {
  const [count, setCount] = useState(end)
  const [shouldAnimate, setShouldAnimate] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const isFirstCheck = useRef(true)

  useEffect(() => {
    if (!startOnView) {
      setCount(0)
      setShouldAnimate(true)
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (isFirstCheck.current) {
            // Already visible on mount (e.g. page refresh) — show final value, no animation
            setCount(end)
          } else if (typeof document !== "undefined" && document.visibilityState !== "visible") {
            // Hidden tab / screenshot / scraper — frames won't run, so show final value
            setCount(end)
          } else {
            // Scrolled into view — animate from 0
            setCount(0)
            setShouldAnimate(true)
          }
          observer.disconnect()
        }
        isFirstCheck.current = false
      },
      { threshold: 0.3 }
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => observer.disconnect()
  }, [startOnView, end])

  useEffect(() => {
    if (!shouldAnimate) return

    // If frames can't run (no rAF support, or the tab is hidden), settle on the
    // final value so the number is never left pinned at 0.
    if (typeof requestAnimationFrame === "undefined") {
      setCount(end)
      return
    }

    if (typeof document !== "undefined" && document.visibilityState !== "visible") {
      setCount(end)
      const onVisible = () => {
        if (document.visibilityState === "visible") setCount(end)
      }
      document.addEventListener("visibilitychange", onVisible, { once: true })
      return () => document.removeEventListener("visibilitychange", onVisible)
    }

    let startTime: number
    let animationFrame: number

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime
      const progress = Math.min((currentTime - startTime) / duration, 1)

      const easeOutExpo = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress)
      const factor = Math.pow(10, decimals)
      setCount(Math.floor(easeOutExpo * end * factor) / factor)

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate)
      } else {
        setCount(end)
      }
    }

    animationFrame = requestAnimationFrame(animate)

    return () => cancelAnimationFrame(animationFrame)
  }, [shouldAnimate, end, duration, decimals])

  return { count, ref }
}
